import {
  ACTION,
  Button,
  ButtonVariants,
  Dialog,
  type FormControlSizes,
  MixpanelEvent,
  Text,
  Tooltip,
  useCallbackRef,
  useDisclosure,
  useMixpanel,
} from '@talos/kyoko';
import { useRoleAuth } from '../../../../hooks';
import { useAppStateDispatch, useAppStateSelector } from '../../../../providers/AppStateProvider';

import { useManualRFQPricingContext } from '../ManualRFQPricingContextProvider';

export const QuoteActionButtons = ({ size }: { size: FormControlSizes }) => {
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const { selectors, actions, service } = useManualRFQPricingContext();
  const state = useAppStateSelector(selectors.selectState);
  const canSendQuoteDetails = useAppStateSelector(selectors.selectCanSendQuote);
  const canRejectQuoteDetails = useAppStateSelector(selectors.selectCanRejectQuote);
  const authorizedToRespond = isAuthorized(ACTION.MANUAL_RFQ_PRICING);
  const dispatch = useAppStateDispatch();

  const confirmRejectionDialog = useDisclosure();

  const handleRejectQuote = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CancelCustomerQuote);
    service.cancelQuote();
  });

  const handleUpdateQuote = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.UpdateCustomerQuote);
    service.updateQuote();
  });

  return (
    <>
      <Tooltip tooltip={canRejectQuoteDetails.text}>
        <Button
          size={size}
          data-testid="reject-quote-button"
          onClick={() => confirmRejectionDialog.open()}
          variant={ButtonVariants.Negative}
          loading={state.isLoading}
          disabled={!authorizedToRespond || !canRejectQuoteDetails.canRejectQuote}
        >
          Reject
        </Button>
      </Tooltip>
      <Tooltip tooltip={canSendQuoteDetails.text}>
        <Button
          size={size}
          data-testid="send-quote-button"
          onClick={handleUpdateQuote}
          onMouseOver={() => dispatch(actions.touchAll(undefined))}
          variant={ButtonVariants.Primary}
          loading={state.isLoading}
          disabled={!authorizedToRespond || !canSendQuoteDetails.canSendQuote}
        >
          Send Quote
        </Button>
      </Tooltip>
      <Dialog
        {...confirmRejectionDialog}
        onConfirm={handleRejectQuote}
        title="Confirm rejection"
        confirmLabel="Reject quote"
        maxWidth="400px"
        stretchButtons
      >
        <Text textAlign="left">
          Are you sure that you want to reject this quote? You will not be able to undo this action and your
          counterparty will see that you rejected this quote.
        </Text>
      </Dialog>
    </>
  );
};
